import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { historyBack } from '../../utils'
import { NotificationService } from '../../services'
import { Parameter } from 'depoto-core/src/entities'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'
import { TitleService } from '../../services'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { ParameterService } from '../../services/entity-services/parameter.service'
import { updateCachedListQueries } from '../../services/query-cache.service'

export const parameterSchema = {
  id: null,
  name: null,
  type: null,
  unit: null,
  text: null,
  externalId: null,
  enumValues: [],
}

@Component({
  selector: 'app-parameter',
  templateUrl: 'parameter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ParameterComponent implements CheckDataChanges, OnDestroy {
  private parameterService = inject(ParameterService)
  private queryClient = injectQueryClient()

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  private initialFormValue = signal<any>(null)
  submitted = signal(false)

  parameterTypes: string[] = ['number', 'boolean', 'text', 'enum', 'enum_multi']
  enumValuesString = ''

  parameterForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
    type: new FormControl({ value: '', disabled: false }, [Validators.required]),
    unit: new FormControl({ value: '', disabled: false }, []),
    text: new FormControl({ value: '', disabled: false }, []),
    externalId: new FormControl({ value: '', disabled: false }, []),
    enumValues: new FormControl({ value: [], disabled: false }, []),
  })

  id = computed(() => Number(this.route.snapshot.params['id']))

  readonly queryParameter = injectQuery(() => ({
    queryKey: ['parameter', this.id()],
    queryFn: async () => {
      if (this.id()) {
        return await this.parameterService.getParameter(this.id(), parameterSchema)
      } else {
        return new Parameter()
      }
    },
    onSuccess: data => {
      this.enumValuesString = data['enumValues'] && data['enumValues']?.length ? data['enumValues'].join('\n') : ''
      this.titleService.updateTitleWithObject(data['id'], data['name'], 'title.parameter-id')
      this.fillFormValue(data)
    },
  }))

  createParameter = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.parameterForm.invalid) {
        throw new Error('Form is invalid')
      }
      // todo ?
      if (this.parameterForm.value.type === 'enum' || this.parameterForm.value.type === 'enum_multi') {
        this.parameterForm.value.enumValues = this.enumValuesString.split('\n')
      }
      const n = this.getDataToSave()

      return await this.parameterService.createParameter(n, parameterSchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.parameterForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.parameterForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('ALERT PARAMETER CREATED'))

      // Optionally keep invalidation for full listing pages
      this.router.navigate(['/parameter', data['id']], { replaceUrl: true })

      // Update detail cache for this parameter
      this.queryClient.setQueryData(['parameter', Number(data['id'])], data)
      // OPTIONS: update options/list caches so selects/filters see the new parameter immediately
      updateCachedListQueries(this.queryClient, ['parameters', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })
      // LISTING ['parameters', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removeParameterListings()

      this.router.navigate(['/parameter/', data['id']], { replaceUrl: true })
    },
  }))

  updateParameter = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)
      if (this.parameterForm.invalid) {
        throw new Error('Form is invalid')
      }

      if (this.parameterForm.value.type === 'enum' || this.parameterForm.value.type === 'enum_multi') {
        this.parameterForm.value.enumValues = this.enumValuesString.split('\n')
      }

      const parameterDataToSave = this.getDataToSave()
      return this.parameterService.updateParameter(parameterDataToSave, parameterSchema)
    },
    onSuccess: async data => {
      this.submitted.set(false)
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.parameterForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.parameterForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('ALERT PARAMETER UPDATED'))

      // Update detail cache for this parameter
      this.queryClient.setQueryData(['parameter', data['id']], data)
      // OPTIONS: update options cache value for this parameter
      updateCachedListQueries(this.queryClient, ['parameters', 'options'], data, { mode: 'update' })
      // LISTING ['parameters', sortProp, sortDir]: On UPDATE → if id is present in cached pages, refetch that exact query; otherwise do nothing
      this.removeParameterListings()

      this.router.navigate(['/parameter', data['id']])
    },
  }))

  deleteParameter = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)

      const confirmation = confirm(
        this.translateService.instant('parameter.delete.confirmation', { name: this.parameterForm.value.name })
      )
      if (confirmation) {
        await this.parameterService.deleteParameter(this.queryParameter.data()).then(() => {
          this.notificationService.success(this.translateService.instant('ALERT PARAMETER DELETED'))

          // Prune deleted parameter from options/list caches
          const idStr = String(this.router.url.split('/')[2])
          // todo const idStr = String(this.route.snapshot.params['id'])
          updateCachedListQueries(this.queryClient, ['parameters', 'options'], { id: Number(idStr) } as any, {
            mode: 'delete',
          })
          // LISTING ['parameters', sortProp, sortDir]: On DELETE → if id is present in cached pages, remove it; otherwise do nothing
          this.removeFromListingsIfPresent(Number(idStr))

          this.router.navigate(['/parameters'], { replaceUrl: true })
        })
      }
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  loading = computed(() => {
    return this.queryParameter.isFetching() || this.queryParameter.isPending()
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private titleService: TitleService
  ) {
    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different parameter ids. This mutates the app-wide Router's routeReuseStrategy though, not
    // something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
    // Populate the form whenever query data becomes available/changes (works for cache hits too)
    effect(() => {
      const d = this.queryParameter.data()
      const id = this.route.snapshot.params['id']
      if (id && d && this.parameterForm.pristine) {
        this.fillFormValue(d)
        this.enumValuesString = Array.isArray((d as any)?.enumValues) ? (d as any).enumValues.join('\n') : ''
      }
    })
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
  }

  public fillFormValue(parameter) {
    // const data = this.queryParameter.data() as any
    const value = {
      name: parameter?.name ?? '',
      type: parameter?.type ?? '',
      unit: parameter?.unit ?? '',
      text: parameter?.text ?? '',
      externalId: parameter?.externalId ?? '',
      // keep enumValues control value for completeness, though we primarily drive via enumValuesString
      enumValues: Array.isArray(parameter?.enumValues) ? parameter.enumValues : [],
    }
    this.enumValuesString =
      Array.isArray(parameter?.enumValues) && parameter.enumValues.length ? parameter.enumValues.join('\n') : ''
    this.parameterForm.patchValue(value, { emitEvent: false })
    // Snapshot initial values (including enumValuesString) and mark pristine
    this.initialFormValue.set(structuredClone({ ...value, enumValuesString: this.enumValuesString }))
    this.parameterForm.markAsPristine()
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.parameterForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    // Before snapshot is ready
    if (!initial) {
      return this.parameterForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))
    const currentEnumStr = norm(this.enumValuesString)
    const initialEnumStr = norm(initial?.enumValuesString)

    return (
      norm(current?.name) !== norm(initial?.name) ||
      norm(current?.type) !== norm(initial?.type) ||
      norm(current?.unit) !== norm(initial?.unit) ||
      norm(current?.externalId) !== norm(initial?.externalId) ||
      // Only consider enum values when type is enum/enum_multi
      ((current?.type === 'enum' || current?.type === 'enum_multi') && currentEnumStr !== initialEnumStr)
    )
  }

  getDataToSave() {
    const current = this.parameterForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    if (this.id()) patch.id = Number(this.id())

    // Always include required fields
    patch.name = current.name
    patch.type = current.type

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))

    // Optional fields — include only when changed
    if (norm(current.unit) !== norm(initial?.unit)) {
      patch.unit = current.unit
    }
    if (norm(current.externalId) !== norm(initial?.externalId)) {
      patch.externalId = current.externalId
    }
    if (norm(current.text) !== norm(initial?.text)) {
      patch.text = current.text
    }

    // enumValues: include only for enum/enum_multi and only when changed
    if (current.type === 'enum' || current.type === 'enum_multi') {
      const currEnum = (this.enumValuesString || '')
        .split('\n')
        .map(s => s.trim())
        .filter(s => s.length > 0)
      const initEnum = (initial?.enumValuesString || '')
        .split('\n')
        .map((s: string) => s.trim())
        .filter((s: string) => s.length > 0)
      const eqArrays = (a: string[], b: string[]) => {
        if (a.length !== b.length) return false
        for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
        return true
      }
      if (!eqArrays(currEnum, initEnum)) {
        patch.enumValues = currEnum
      }
    }

    return patch
  }

  // ===== Helpers for cache handling of parameter listings =====
  private isParametersListingKey(key: unknown[]): boolean {
    // We already filtered with getQueriesData({ queryKey: ['parameters'] }),
    // so only ensure the key is an array that starts with 'parameters'.
    return Array.isArray(key) && key[0] === 'parameters'
  }

  private listingContainsId(cached: any, id: number | string): boolean {
    const idStr = String(id)
    if (!cached) return false

    // Infinite query shape: { pages: [...] }
    if (Array.isArray(cached.pages)) {
      for (const page of cached.pages) {
        const items = Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : undefined
        if (!Array.isArray(items)) continue
        if (items.some((it: any) => String(it?.id) === idStr)) return true
      }
      return false
    }

    // Plain array shape
    if (Array.isArray(cached)) {
      return cached.some((it: any) => String(it?.id) === idStr)
    }

    // Object with items array
    if (Array.isArray(cached?.items)) {
      return cached.items.some((it: any) => String(it?.id) === idStr)
    }

    return false
  }

  private removeIdFromCachedListing(key: unknown[], id: number | string): void {
    const cached: any = this.queryClient.getQueryData(key as any)
    if (!cached) return
    const idStr = String(id)

    // Infinite query shape: { pages: [...] }
    if (Array.isArray(cached.pages)) {
      const newPages = cached.pages.map((page: any) => {
        if (Array.isArray(page)) {
          return page.filter((it: any) => String(it?.id) !== idStr)
        }
        if (Array.isArray(page?.items)) {
          return { ...page, items: page.items.filter((it: any) => String(it?.id) !== idStr) }
        }
        return page
      })
      this.queryClient.setQueryData(key as any, { ...cached, pages: newPages })
      return
    }

    // Plain array shape
    if (Array.isArray(cached)) {
      this.queryClient.setQueryData(
        key as any,
        cached.filter((it: any) => String(it?.id) !== idStr)
      )
      return
    }

    // Object with items array
    if (Array.isArray(cached?.items)) {
      this.queryClient.setQueryData(key as any, {
        ...cached,
        items: cached.items.filter((it: any) => String(it?.id) !== idStr),
      })
      return
    }
  }

  private removeParameterListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['parameters'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  // DELETE → if present in cached listings, remove it; otherwise do nothing
  private removeFromListingsIfPresent(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['parameters'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isParametersListingKey(key as unknown[])) {
        continue
      }
      if (!this.listingContainsId(cached, id)) {
        continue
      }
      this.removeIdFromCachedListing(key as unknown[], id)
    }
  }

  goBack(): void {
    historyBack()
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.parameterForm.controls
  }
}
